// contexts/AuthProvider.tsx
import React, { useCallback, useEffect, useReducer } from 'react';
import axios from 'axios';
import { AuthContext, authReducer, initialAuthState } from './AuthContext';
import { authService } from '@/api/core/auth/authService';
import { clearChameleonSession } from '@/api/core/chameleonState';
import type { AuthUser, LoginSessionUser } from '@/types/auth';
import { type EntityType, entityTypeFromKey, type PermissionLevel } from '@/types/common';

const reconcileViewerIdentity = async (storedUser: LoginSessionUser): Promise<LoginSessionUser> => {
    const viewer = await authService.getViewerIdentity();
    const entityType: EntityType = viewer.entity_type
        ? (entityTypeFromKey(viewer.entity_type) ?? (viewer.entity_type.toUpperCase() as EntityType))
        : 'SYSTEM';
    const isSuperAdmin = viewer.role_id === 1;
    const isAdmin = isSuperAdmin || entityType !== 'SYSTEM';
    const permissionLevel: PermissionLevel = isSuperAdmin ? 'SUPER_ADMIN' : isAdmin ? 'ADMIN' : 'USER';

    return {
        ...storedUser,
        email: viewer.email,
        name: viewer.entity_name ?? storedUser.name,
        role: isSuperAdmin ? 'ROLE_ADMIN' : `ROLE_${entityType}${isAdmin ? '_ADMIN' : ''}`,
        role_id: viewer.role_id ?? storedUser.role_id,
        entity_id: viewer.entity_id ?? 0,
        entity_type: entityType,
        country_code: viewer.country_code,
        country_list: viewer.country_list,
        partition_id: viewer.partition_id,
        permission_level: permissionLevel,
        isAdmin,
        isSuperAdmin,
    };
};

const enhanceUserData = (userData: LoginSessionUser | AuthUser): AuthUser => {
    return {
        ...userData,
        partition_id: userData.partition_id ?? null,
        isMultiCountry: Array.isArray(userData.country_list) && userData.country_list.length > 1,
        isAdTechUser: userData.entity_type === 'ADTECH',
        isBroadcaster: userData.entity_type === 'BROADCASTER',
        isAgency: userData.entity_type === 'AGENCY',
        isCustomer: userData.entity_type === 'CUSTOMER',
        isDistributor: userData.entity_type === 'DISTRIBUTOR',
        isSuperUser: userData.isSuperAdmin,
        isEntityAdmin: userData.permission_level === 'ADMIN',
        isPartitioned: userData.partition_id != null,
        entityType: userData.entity_type,
        homeCountry: userData.country_code,
        operationalCountries: userData.country_list || [userData.country_code].filter(Boolean),
    };
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [authState, dispatch] = useReducer(authReducer, initialAuthState);

    const login = useCallback(async (email: string, password: string) => {
        try {
            dispatch({ type: 'SET_LOADING', payload: true });
            const result = await authService.login(email, password);

            if (result.status === 'success' && result.user) {
                const enhancedUser = enhanceUserData(result.user);
                localStorage.setItem('user', JSON.stringify(enhancedUser));
                dispatch({ type: 'LOGIN', payload: { user: enhancedUser, role: enhancedUser.role } });
            } else {
                dispatch({ type: 'AUTH_ERROR', payload: result.error ?? 'Login failed' });
            }

            return result;
        } catch (error) {
            const msg = error instanceof Error ? error.message : 'Login failed';
            dispatch({ type: 'AUTH_ERROR', payload: msg });
            return { status: 'failure', error: msg };
        }
    }, []);

    const logout = useCallback(async () => {
        localStorage.removeItem('user');
        clearChameleonSession();
        await authService.logout();
        dispatch({ type: 'LOGOUT' });
    }, []);

    const refreshToken = useCallback(async () => {
        try {
            return await authService.refreshToken();
        } catch (error) {
            dispatch({ type: 'LOGOUT' });
            throw error;
        }
    }, []);

    // Bridge: axios interceptor fires this event when it can't recover a 401
    useEffect(() => {
        const handleForceLogout = async () => {
            localStorage.removeItem('user');
            clearChameleonSession();
            try {
                await authService.logout();
            } catch {
                // Best effort — session is gone regardless
            }
            dispatch({ type: 'LOGOUT' });
        };

        window.addEventListener('auth:force-logout', handleForceLogout);
        return () => window.removeEventListener('auth:force-logout', handleForceLogout);
    }, []);

    // Rehydrate auth state from localStorage on mount
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                localStorage.removeItem('access_token');
                localStorage.removeItem('refreshToken');

                const raw = localStorage.getItem('user');
                const storedUser: LoginSessionUser | null = raw ? JSON.parse(raw) : null;

                if (storedUser && authService.isAuthenticated()) {
                    // /me is authoritative when it answers, but it refines the login snapshot —
                    // it is not a gate. A backend without the route (404) or having a bad moment
                    // (5xx, network) must not log everyone out on reload. A dead session (401)
                    // still ends the session: the interceptor has already fired auth:force-logout.
                    const reconciledUser = await reconcileViewerIdentity(storedUser).catch((error: unknown) => {
                        if (axios.isAxiosError(error) && error.response?.status === 401) throw error;
                        console.warn('Viewer identity reconciliation failed; keeping stored session', error);
                        return storedUser;
                    });
                    const enhancedUser = enhanceUserData(reconciledUser);

                    localStorage.setItem('user', JSON.stringify(enhancedUser));

                    dispatch({ type: 'LOGIN', payload: { user: enhancedUser, role: enhancedUser.role } });
                } else {
                    dispatch({ type: 'SET_LOADING', payload: false });
                }
            } catch (error) {
                console.error('Auth initialization error:', error);
                dispatch({ type: 'SET_LOADING', payload: false });
            }
        };

        initializeAuth();
    }, []);

    const contextValue = React.useMemo(
        () => ({ authState, login, logout, refreshToken }),
        [authState, login, logout, refreshToken]
    );

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
